// src/components/onboarding/steps/Step4Operations.tsx
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { OnboardingStepProps, BookableService } from '@/types/onboarding'; // Ensure BookableService is defined in this file
// import { Badge } from '@/components/ui/badge'; // No longer needed for URLs
import { Pencil, Trash2, Plus, Save } from 'lucide-react'; // Ensure lucide-react is installed
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    // AlertDialogTrigger, // Trigger is handled manually via Button onClick
} from '@/components/ui/alert-dialog';


const Step4Operations: React.FC<OnboardingStepProps> = ({ data, updateData, goToNextStep, goToPrevStep }) => {
    // Explicitly type the errors state, removed urls error
    const [errors, setErrors] = useState<{ services?: string; defaultDuration?: string }>({});

    // --- Service with Duration Handling ---
    const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
    const [editingServiceIndex, setEditingServiceIndex] = useState<number | null>(null);
    const [currentServiceName, setCurrentServiceName] = useState('');
    const [currentServiceDuration, setCurrentServiceDuration] = useState<number | string>(30); // Default to 30, allow empty string during input
    const [currentServiceDescription, setCurrentServiceDescription] = useState('');

    const handleOpenServiceModal = (index: number | null = null) => {
        if (index !== null && data.bookableServices[index]) {
            // Editing existing service
            setEditingServiceIndex(index);
            setCurrentServiceName(data.bookableServices[index].name);
            setCurrentServiceDuration(data.bookableServices[index].duration);
            setCurrentServiceDescription(data.bookableServices[index].description ?? '');
        } else {
            // Adding new service
            setEditingServiceIndex(null);
            setCurrentServiceName('');
            setCurrentServiceDuration(30); // Reset to default for new service modal
            setCurrentServiceDescription('');
        }
        setIsServiceModalOpen(true);
        // Clear any previous service-related errors when opening the modal
        setErrors(prev => ({ ...prev, services: undefined }));
    };

    const handleSaveService = () => {
        const trimmedName = currentServiceName.trim();
        // Ensure duration is treated as a number, even if input state holds string temporarily
        const durationNum = Number(currentServiceDuration);

        // Validation within the modal save action
        if (!trimmedName) {
            setErrors(prev => ({ ...prev, services: 'Service name cannot be empty.' }));
            return;
        }
        if (isNaN(durationNum) || durationNum <= 0 || !Number.isInteger(durationNum)) {
            setErrors(prev => ({ ...prev, services: 'Duration must be a positive whole number (minutes).' }));
            return;
        }

        const newService: BookableService = { name: trimmedName, duration: durationNum, description: currentServiceDescription.trim() };
        let updatedServices: BookableService[];

        if (editingServiceIndex !== null) {
             // Check if edited name duplicates *another* existing service name
             if (data.bookableServices.some((s, index) => index !== editingServiceIndex && s.name.toLowerCase() === trimmedName.toLowerCase())) {
                setErrors(prev => ({ ...prev, services: `Another service named "${trimmedName}" already exists.` }));
                return;
            }
            // Update the service at the specific index
            updatedServices = data.bookableServices.map((service, index) =>
                index === editingServiceIndex ? newService : service
            );
        } else {
            // Check for duplicate service name before adding a *new* service
            if (data.bookableServices.some(s => s.name.toLowerCase() === trimmedName.toLowerCase())) {
                setErrors(prev => ({ ...prev, services: `Service "${trimmedName}" already exists.`}));
                return;
            }
            // Add the new service to the existing list
            updatedServices = [...data.bookableServices, newService];
        }

        // Update the main state via the passed function
        updateData({ bookableServices: updatedServices });

        // Close modal and reset modal state
        setIsServiceModalOpen(false);
        setEditingServiceIndex(null);
        setCurrentServiceName('');
        setCurrentServiceDuration(30); // Reset for next potential add
        setCurrentServiceDescription('');
        setErrors(prev => ({ ...prev, services: undefined })); // Clear service error on successful save
    };

    const handleRemoveService = (indexToRemove: number) => {
         updateData({
             bookableServices: data.bookableServices.filter((_, index) => index !== indexToRemove)
         });
         // If a global service error was shown (e.g., "add at least one"), clear it if list is no longer empty
         if (data.bookableServices.length > 1) { 
             setErrors(prev => ({ ...prev, services: undefined }));
         }
    };
    // --- End Service with Duration Handling ---

    const validateAndProceed = () => {
        const newErrors: Pick<typeof errors, 'services' | 'defaultDuration'> = {}; // Only include relevant error types

        // Validate Services
        if (!data.bookableServices || data.bookableServices.length === 0) {
            newErrors.services = 'Please add at least one service with its duration.';
        }

        // Validate Default Duration
        const defaultDurationNum = Number(data.defaultAppointmentDurationMinutes);
        if (data.defaultAppointmentDurationMinutes === null || data.defaultAppointmentDurationMinutes === undefined || isNaN(defaultDurationNum) || defaultDurationNum <= 0 || !Number.isInteger(defaultDurationNum)) {
            newErrors.defaultDuration = 'Default duration must be a positive whole number (minutes).';
        }
        
        setErrors(prevErrors => ({ ...prevErrors, ...newErrors }));

        if (Object.values(newErrors).every(error => !error)) { // Check only relevant errors
           goToNextStep();
        }
    };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-gray-800 dark:text-gray-100">Business Operations & Services</h2>

      <div className="space-y-8">
        <div>
          <Label htmlFor="businessHoursRaw" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            What are your Business Hours? <span className="text-gray-500 text-xs">(Recommended)</span>
          </Label>
          <Textarea
            id="businessHoursRaw"
            value={data.businessHoursRaw ?? ''}
            onChange={(e) => updateData({ businessHoursRaw: e.target.value })}
            placeholder="e.g., Monday-Friday: 9 AM - 5 PM\nSaturday: 10 AM - 2 PM\nSunday: Closed"
            className="mt-1"
            rows={4}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Crucial for the AI to understand your availability. You can refine this later.
          </p>
        </div>

        <Card>
          <CardHeader>
              <div className="flex justify-between items-center">
                  <CardTitle>Bookable Services</CardTitle>
                  <Button type="button" variant="outline" size="sm" onClick={() => handleOpenServiceModal()}>
                      <Plus size={16} className="mr-2" /> Add Service
                  </Button>
              </div>
              <CardDescription>
                  List the main services or appointments your AI can book, along with their standard durations. Each service can have its own specific instructions for your AI.
              </CardDescription>
          </CardHeader>
          <CardContent>
              {errors.services && !isServiceModalOpen && <p className="text-xs text-red-600 mb-2">{errors.services}</p>}
              {data.bookableServices.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4 border-2 border-dashed rounded-md">
                      No services added yet. Click "Add Service".
                  </p>
              ) : (
                  <div className="space-y-2">
                      {data.bookableServices.map((service, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded-md bg-slate-50 dark:bg-slate-800">
                              <div>
                                  <p className="font-medium text-gray-800 dark:text-gray-100">{service.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{service.duration} minutes</p>
                                  {service.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{service.description}</p>}
                              </div>
                              <div className="flex gap-1 sm:gap-2">
                                  <Button type="button" variant="ghost" size="icon" onClick={() => handleOpenServiceModal(index)} className="h-7 w-7" title={`Edit ${service.name}`}>
                                      <Pencil size={14} />
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveService(index)} className="text-red-500 hover:text-red-700 h-7 w-7" title={`Remove ${service.name}`}>
                                      <Trash2 size={14} />
                                  </Button>
                              </div>
                          </div>
                      ))}
                  </div>
              )}
          </CardContent>
        </Card>

        <div>
          <Label htmlFor="defaultAppointmentDurationMinutes" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Default Appointment Duration (Fallback) <span className="text-red-500">*</span>
          </Label>
          <Input
            id="defaultAppointmentDurationMinutes"
            type="number"
            value={data.defaultAppointmentDurationMinutes ?? ''}
            onChange={(e) => {
                const valueStr = e.target.value;
                const num = parseInt(valueStr, 10);
                updateData({ defaultAppointmentDurationMinutes: (valueStr === '' || isNaN(num)) ? undefined : num });
            }}
            placeholder="e.g., 30"
            className={`mt-1 w-32 ${errors.defaultDuration ? 'border-red-500' : ''}`}
            min="1" 
            step="1"
          />
           <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
             In minutes. Used if a specific service duration isn't applicable or found. Must be a positive number.
           </p>
           {errors.defaultDuration && <p className="text-xs text-red-600 mt-1">{errors.defaultDuration}</p>}
        </div>

        <div>
          <Label htmlFor="businessAddress" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Business Address <span className="text-gray-500 text-xs">(Optional)</span>
          </Label>
          <Textarea
            id="businessAddress"
            value={data.businessAddress ?? ''}
            onChange={(e) => updateData({ businessAddress: e.target.value })}
            placeholder="e.g., 123 Main St, Anytown, CA 90210"
            className="mt-1"
            rows={3}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Useful if your AI needs to provide directions or confirm location.
          </p>
        </div>
        
        {/* Website Link(s) for AI Knowledge section REMOVED */}

      </div>

      <AlertDialog open={isServiceModalOpen} onOpenChange={setIsServiceModalOpen}>
          <AlertDialogContent>
              <AlertDialogHeader>
                  <AlertDialogTitle>{editingServiceIndex !== null ? 'Edit Service' : 'Add New Service'}</AlertDialogTitle>
                  <AlertDialogDescription>
                      Define a bookable service and provide service-specific instructions for how your AI should handle this particular service's bookings, requirements, and customer interactions.
                  </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-4 py-4">
                  <div>
                      <Label htmlFor="serviceNameModal" className="text-sm font-medium">Service Name</Label>
                      <Input
                          id="serviceNameModal"
                          value={currentServiceName}
                          onChange={(e) => setCurrentServiceName(e.target.value)}
                          placeholder="e.g., Initial Consultation"
                          className="mt-1"
                          maxLength={100}
                      />
                  </div>
                  <div>
                      <Label htmlFor="serviceDurationModal" className="text-sm font-medium">Duration (minutes)</Label>
                      <Input
                          id="serviceDurationModal"
                          type="number"
                          value={currentServiceDuration}
                          onChange={(e) => setCurrentServiceDuration(e.target.value === '' ? '' : Number(e.target.value))}
                          placeholder="e.g., 60"
                          className="mt-1"
                          min="1"
                          step="1"
                      />
                  </div>
                  <div>
                      <Label htmlFor="serviceDescriptionModal" className="text-sm font-medium">Service-Specific AI Instructions (Optional)</Label>
                      <Textarea
                          id="serviceDescriptionModal"
                          value={currentServiceDescription}
                          onChange={(e) => setCurrentServiceDescription(e.target.value)}
                          placeholder="Instructions specific to this service only. E.g., 'Always ask about insurance coverage first. $150 deposit required. 24hr cancellation policy. Bring ID and insurance card.'"
                          className="mt-1"
                          rows={3}
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          <strong>💡 Service-Specific Only:</strong> These instructions apply only when customers ask about or book this specific service. For business-wide rules (like "all new customers get 10% off"), you can add those later in your settings.
                      </p>
                  </div>
                  {errors.services && isServiceModalOpen && <p className="text-xs text-red-600">{errors.services}</p>}
              </div>
              <AlertDialogFooter>
                  <AlertDialogCancel type="button" onClick={() => {
                      setIsServiceModalOpen(false);
                      setCurrentServiceDescription('');
                      setErrors(prev => ({ ...prev, services: undefined })); // Clear modal-specific error on cancel
                      }}>Cancel</AlertDialogCancel>
                  <Button type="button" onClick={handleSaveService}>
                      <Save size={16} className="mr-2" /> {editingServiceIndex !== null ? 'Save Changes' : 'Add Service'}
                  </Button>
              </AlertDialogFooter>
          </AlertDialogContent>
      </AlertDialog>


      <p className="text-sm text-center text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 p-3 rounded-md mt-8">
          You can add more details and refine AI knowledge later in your settings. This includes global business rules (like "all new customers get 10% off") that apply to every call.
      </p>

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={goToPrevStep} disabled={!goToPrevStep}>
          Back
        </Button>
        <Button onClick={validateAndProceed}>Next</Button>
      </div>
    </div>
  );
};

export default Step4Operations;